import { AfterViewInit, Component, computed, ElementRef, inject, model, OnChanges, OnInit, signal, SimpleChanges, ViewChild, ChangeDetectionStrategy, HostListener, ChangeDetectorRef } from '@angular/core';
import { catchError, of, debounceTime, distinctUntilChanged } from 'rxjs';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BookAppointmentService } from '../book-appointment.service';
import { DatePipe } from '@angular/common';
import { SessionStorageService } from '../../services/session-storage.service';
import { AppService } from '../../services/app.service';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix, MatError, MatHint, MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker, MatCalendar } from '@angular/material/datepicker';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { IResource, IService, ITimeslot, Service, TimeslotRequest } from 'src/app/services/client.service';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-appointment',
  templateUrl: './appointment.component.html',
  styleUrl: './appointment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    MatFormField,
    MatLabel,
    MatHint,
    MatOption,
    ReactiveFormsModule,
    MatSelect,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatError,
    MatChipsModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCardModule,
    MatCalendar,
    DatePipe,
    MatProgressSpinner
  ],
  providers: [
    BookAppointmentService,
    SessionStorageService,
    AppService,
    DatePipe,
    MatDialog
  ],
})
export class AppointmentComponent implements OnChanges, OnInit, AfterViewInit {

  readonly panelOpenState = signal(false);
  resourcesForm!: FormGroup;
  serviceAdvisorList!: IResource[];
  mobilityVehicleList!: IResource[];
  selectedServiceAdvisor!: IResource;
  selectedMobilityVehicle!: IResource;
  selectedServices!: IService[];
  serviceAdvisorTimeslots: ITimeslot[] = [];
  mobilityVehicleTimeslots: ITimeslot[] = [];
  showServiceAdvisorTimesheets: boolean = true;
  showMobilityVehicleTimeSheets: boolean = false;
  isPageLoading: boolean = true;

  @ViewChild('autocompleteInput') autocompleteInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  selectedServiceListObject!: IService[];
  currentService = model('');
  services = signal(['new service']);
  allServices: string[] = [];
  filteredServices = computed(() => {
    const currentService = this.currentService().toLowerCase();
    return currentService
      ? this.allServices.filter(service => service.toLowerCase().includes(currentService))
      : this.allServices.slice();
  });
  readonly announcer = inject(LiveAnnouncer);

  pickappointment: string = $localize`Pick an appointment date`;
  timeslotwarning: string = $localize`Select a suitable timeslot`;
  pickdate: string = $localize`Pick a date`;
  noDatesAvailable: string = $localize`No dates available for the selected Service advisor. Please try selecting another Service advisor or another outlet.`;
  errorMessage: string = $localize`Online Appointment Booking not supported. Please contact dealer directly.`;
  monthsList: string[] = [];
  selectedMonth!: string;
  selectedTimeSlot!: string;
  timeslotForm!: FormGroup;
  pickupServiceForm!: FormGroup;
  minDate!: Date;
  servicesResponse!: IService[];
  resourcesResponse!: IResource[];
  appointmentDate: Date | null = null;
  availableDates: Date[] = [];
  selectedDate: Date | null = null;
  availableTimeslots: string[] = [];
  selectedTimeslot: string | null = null;
  timeslotPages: string[][] = [];
  currentPage: number = 0;
  itemsPerPage: number = 9;
  myFilter!: (date: Date | null) => boolean;
  showTimeslotWarning: boolean = false;
  showDateWarning: boolean = false;
  isLargeScreen = signal(window.innerWidth > 900);
  isLoading = true;
  datesErrorMessage: string | null = null;
  private timeslotRequestId = 0;
  private datesErrorTimer: any = null;

  private touchStartX: number = 0;
  private touchEndX: number = 0;
  private minSwipeDistance: number = 50;
  public isDataLoading = true;
  public validatorsRestored = false;
  public formTouched = false;
  private localeCache = {
    locale: '',
    dateHint: '',
    hour12Pref: null as boolean | null
  };

  constructor(
    private fb: FormBuilder,
    private bookAppointmentService: BookAppointmentService,
    private sessionStorageService: SessionStorageService,
    private appService: AppService,
    public dialog: MatDialog,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  @HostListener('window:resize')
  onWindowResize(): void {
    const newIsLargeScreen = window.innerWidth > 900;
    if (this.isLargeScreen() !== newIsLargeScreen) {
      this.isLargeScreen.set(newIsLargeScreen);
      this.cdr.markForCheck();
    }
    this.updateItemsPerPageForViewport();
  }

  trackByServiceId(index: number, service: IService): string {
    return service.id || index.toString();
  }

  trackByResourceId(index: number, resource: IResource): string {
    return resource.id || index.toString();
  }

  trackByTimeslot(index: number, timeslot: string): string {
    return timeslot;
  }

  trackByIndex(index: number): number {
    return index;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.allServices = []
    if (this.servicesResponse) {
      this.servicesResponse.forEach(element => this.allServices.push(element.description ?? ''));
      this.filteredServices = computed(() => {
        const currentService = this.currentService().toLowerCase();
        return currentService
          ? this.allServices.filter(service => service.toLowerCase().includes(currentService))
          : this.allServices.slice();
      });
    }

    if (this.resourcesResponse) {
      this.serviceAdvisorList = this.resourcesResponse.filter((element: IResource) => element.type === 'ServiceAdvisor');
      this.mobilityVehicleList = this.resourcesResponse.filter((element: IResource) => element.type === 'MobilityVehicle');
    }

    this.services.update(services => {
      services.forEach((element: string) => {
        if (Array.isArray(this.servicesResponse)) {
          const foundService = this.servicesResponse.find((ele: IService) => ele.description === element);
          this.selectedServices = foundService ? [foundService] : [];
        }
      })
      return [...services];
    });
  }


  ngOnInit(): void {
    this.isDataLoading = true;
    this.initForms();

    const sessionData = this.getSessionData();
    const reqObject = this.buildResourcesRequest(sessionData);
    this.loadResourcesAndDefaults(reqObject, sessionData);
    this.myFilter = this.filterDates.bind(this);
    this.updateLocaleCache();
    this.updateItemsPerPageForViewport();
    if (sessionData?.appointment?.appointmentDate) {
      this.selectedDate = new Date(sessionData.appointment.appointmentDate);
      this.resourcesForm.patchValue({
        preferredDateControl: this.selectedDate
      });
    }
  }


ngAfterViewInit(): void {
  const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
  const sessionData = sessionDataString ? JSON.parse(sessionDataString) : null;

  if (sessionData?.appointment) {
    const currentValues = this.resourcesForm.value;
    const patchValues: any = {};

    if (sessionData.appointment.serviceAdvisor?.Id && !currentValues.serviceadvisorIdControl) {
      patchValues.serviceadvisorIdControl = sessionData.appointment.serviceAdvisor.Id;
    }
    if (sessionData.appointment.selectedTimeslot && !currentValues.serviceAdvisorTimeslot) {
      patchValues.serviceAdvisorTimeslot = sessionData.appointment.selectedTimeslot;
    }

    if (Object.keys(patchValues).length > 0) {
      this.resourcesForm.patchValue(patchValues);
    }

    if (this.selectedDate && (this.serviceAdvisorTimeslots.length > 0 || this.mobilityVehicleTimeslots.length > 0)) {
      this.loadTimeslotsForDate(this.selectedDate);
    }

    if (sessionData.appointment.selectedTimeslot && !this.selectedTimeslot) {
      this.selectedTimeslot = sessionData.appointment.selectedTimeslot;
      this.resourcesForm.patchValue({
        serviceAdvisorTimeslot: this.selectedTimeslot
      });
    }

    setTimeout(() => {
      this.restoreFormValidationState();
      this.cdr.markForCheck();
    }, 0);
  }
}


  public isInitialLoadComplete(): boolean {
    return !this.isDataLoading && this.validatorsRestored && this.serviceAdvisorList?.length > 0;
  }

  private initForms(): void {
    this.minDate = new Date();
    this.pickupServiceForm = this.fb.group({
      yes: [false],
      no: [true]
    });

    const sessionData = this.getSessionData();
    const appointmentData = sessionData?.appointment || {};

    this.resourcesForm = this.fb.group({
      serviceadvisorIdControl: [appointmentData.serviceAdvisorId || null],
      serviceAdvisorTimeslot: [appointmentData.selectedTimeslot || null],
      preferredDateControl: [appointmentData.appointmentDate ? new Date(appointmentData.appointmentDate) : null]
    });

    Object.keys(this.resourcesForm.controls).forEach(key => {
      const control = this.resourcesForm.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });

    this.resourcesForm.valueChanges
      .pipe(
        debounceTime(100),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(() => {
        if (this.formTouched && this.validatorsRestored && !this.isDataLoading) {
          this.updateFormValidation(this.formTouched);
        }
      });
  }

  private restoreValidators(): void {
    if (this.validatorsRestored) return;

    const serviceAdvisorControl = this.resourcesForm.get('serviceadvisorIdControl');
    const timeslotControl = this.resourcesForm.get('serviceAdvisorTimeslot');
    const dateControl = this.resourcesForm.get('preferredDateControl');

    // Commented this validation as it will be required for next phase of development
    // serviceAdvisorControl?.setValidators([Validators.required]);
    timeslotControl?.setValidators([Validators.required]);
    dateControl?.setValidators([Validators.required]);

    serviceAdvisorControl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    timeslotControl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    dateControl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });

    this.validatorsRestored = true;
    this.isDataLoading = false;
    this.cdr.markForCheck();
  }

  validateForm(): boolean {
    if (!this.validatorsRestored) {
      this.restoreValidators();
    }

    this.formTouched = true;
    this.showTimeslotWarning = false;
    this.showDateWarning = false;

    Object.keys(this.resourcesForm.controls).forEach(key => {
      const control = this.resourcesForm.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity({ onlySelf: true });
    });
    this.updateFormValidation(true);

    const hasServiceAdvisor = !!this.resourcesForm.get('serviceadvisorIdControl')?.value;
    const hasDate = !!this.resourcesForm.get('preferredDateControl')?.value;
    const hasTimeslot = !!this.resourcesForm.get('serviceAdvisorTimeslot')?.value;

    if (!hasDate) {
      this.showDateWarning = true;
    }

    if (hasDate && !hasTimeslot) {
      this.showTimeslotWarning = true;
    }

    const isValid = hasServiceAdvisor && hasDate && hasTimeslot;
    const hasActualDate = !!this.selectedDate;
    const hasActualTimeslot = !!this.selectedTimeslot;
    const hasActualAdvisor = !!this.selectedServiceAdvisor;

    this.cdr.markForCheck();
    return isValid && hasActualDate && hasActualTimeslot && hasActualAdvisor;
  }

  private updateFormValidation(showErrors: boolean): void {
    if (this.isDataLoading || !this.validatorsRestored) {
      return;
    }

    const serviceAdvisorControl = this.resourcesForm.get('serviceadvisorIdControl');
    const dateControl = this.resourcesForm.get('preferredDateControl');
    const timeslotControl = this.resourcesForm.get('serviceAdvisorTimeslot');
    const hasServiceAdvisor = !!serviceAdvisorControl?.value;
    const hasDate = !!dateControl?.value;
    const hasTimeslot = !!timeslotControl?.value;

    const isValid = hasServiceAdvisor && hasDate && hasTimeslot;

    if (showErrors && this.formTouched) {
      [serviceAdvisorControl, dateControl, timeslotControl].forEach(control => {
        if (control) {
          control.markAsTouched();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
    this.bookAppointmentService.updateAppointmentFormStatus(isValid);
    this.cdr.markForCheck();
  }

  private getSessionData(): any {
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    return sessionDataString ? JSON.parse(sessionDataString) : {};
  }

  private buildResourcesRequest(sessionData: any): { department: string; } {
    const department = sessionData?.vehicleInfo?.type || '';
    return { department };
  }

  private loadResourcesAndDefaults(reqObject: { department: string; }, sessionData: any): void {
    this.isLoading = true;
    this.datesErrorMessage = null;
    this.appService.getResources(reqObject).subscribe((response: IResource[]) => {
        if (!response || response.length === 0) {
          this.isLoading = false;
          this.isPageLoading = false;
          this.datesErrorMessage = this.errorMessage;
          this.availableDates = [];
          this.availableTimeslots = [];
          this.selectedTimeslot = null;
          this.serviceAdvisorTimeslots = [];
          this.mobilityVehicleTimeslots = [];
          this.resourcesResponse = [];
          this.serviceAdvisorList = [];
          this.mobilityVehicleList = [];
          this.cdr.markForCheck();
          return;
        }
        this.resourcesResponse = response;
        this.serviceAdvisorList = response.filter(el => el.type === 'ServiceAdvisor');
        this.mobilityVehicleList = response.filter(el => el.type === 'MobilityVehicle');
        if (this.serviceAdvisorList.length === 0) {
          this.isLoading = false;
          this.isPageLoading = false;
          this.datesErrorMessage = this.errorMessage;
          this.availableDates = [];
          this.availableTimeslots = [];
          this.selectedTimeslot = null;
          this.serviceAdvisorTimeslots = [];
          this.mobilityVehicleTimeslots = [];
          this.cdr.markForCheck();
          return;
        }
        this.isPageLoading = false;
        if (!sessionData?.appointment?.serviceAdvisorId && this.serviceAdvisorList.length > 0) {
          const defaultAdvisor = this.serviceAdvisorList[0];
          this.selectedServiceAdvisor = defaultAdvisor;
          this.resourcesForm.get('serviceadvisorIdControl')?.setValue(defaultAdvisor.id, { emitEvent: false });
          const updatedSessionData = {
            ...sessionData,
            appointment: {
              ...(sessionData?.appointment || {}),
              serviceAdvisorId: defaultAdvisor.id,
              serviceAdvisorName: defaultAdvisor.description
            }
          };
          this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(updatedSessionData));
          this.getTimeSlots('serviceAdvisor');
        }

        this.setupFromSession(this.getSessionData());
        this.cdr.markForCheck();
      },
      (error) => {
        this.isLoading = false;
        this.isPageLoading = false;
        this.appService.handleErrorMessage(error);
      });
  }

  private restoreFormValidationState(): void {
    if (!this.validatorsRestored) return;
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : {};
    const appointmentData = sessionData?.appointment || {};

    if (appointmentData.serviceAdvisorId &&
      appointmentData.appointmentDate &&
      appointmentData.selectedTimeslot) {
      setTimeout(() => {
        Object.keys(this.resourcesForm.controls).forEach(key => {
          const control = this.resourcesForm.get(key);
          if (control && control.value) {
            control.markAsTouched();
            control.updateValueAndValidity({ onlySelf: true });
          }
        });
        this.formTouched = true;
        this.updateFormValidation(true);
        this.cdr.markForCheck();
      }, 100);
    }
  }

  private setupFromSession(sessionData: any): void {
    this.selectedServiceListObject = sessionData?.appointment?.services || [];
    const appointmentData = sessionData?.appointment || {};

    if (appointmentData.serviceAdvisorId) {
      const advisor = this.serviceAdvisorList?.find(sa => sa.id === appointmentData.serviceAdvisorId);
      if (advisor) {
        this.selectedServiceAdvisor = advisor;
        this.resourcesForm.get('serviceadvisorIdControl')?.setValue(advisor.id, { emitEvent: false });
        if (this.serviceAdvisorTimeslots.length === 0) {
          this.getTimeSlots('serviceAdvisor');
        }
      }
    }
    else if (this.serviceAdvisorList?.length > 0) {
      const defaultAdvisor = this.serviceAdvisorList[0];
      this.selectedServiceAdvisor = defaultAdvisor;
      this.resourcesForm.get('serviceadvisorIdControl')?.setValue(defaultAdvisor.id, { emitEvent: false });

      const updatedSessionData = {
        ...sessionData,
        appointment: {
          ...appointmentData,
          serviceAdvisorId: defaultAdvisor.id,
          serviceAdvisorName: defaultAdvisor.description
        }
      };
      this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(updatedSessionData));
      this.getTimeSlots('serviceAdvisor');
    }

    if (appointmentData.appointmentDate) {
      this.selectedDate = new Date(appointmentData.appointmentDate);
      const dateControl = this.resourcesForm.get('preferredDateControl');
      dateControl?.setValue(this.selectedDate, {
        emitEvent: false,
        onlySelf: true
      });

      if (appointmentData.availableTimeslots?.length > 0) {
        this.availableTimeslots = (appointmentData.availableTimeslots || []).map((s: string) => this.reformatTimeslotString(s));
        this.createTimeslotPages();
      }

      if (appointmentData.selectedTimeslot) {
        this.selectedTimeslot = this.reformatTimeslotString(appointmentData.selectedTimeslot);
        const timeslotControl = this.resourcesForm.get('serviceAdvisorTimeslot');
        timeslotControl?.setValue(this.selectedTimeslot, {
          emitEvent: false,
          onlySelf: true
        });
      }
    }

    setTimeout(() => {
      this.restoreValidators();
      const hasCompleteData = appointmentData.serviceAdvisorId &&
        appointmentData.appointmentDate &&
        appointmentData.selectedTimeslot;

      if (hasCompleteData) {
        this.restoreFormValidationState();
      }
      this.cdr.markForCheck();
    }, 100);
  }

  isFormReady(): boolean {
    this.showTimeslotWarning = false;
    this.showDateWarning = false;

    const hasServiceAdvisor = !!this.selectedServiceAdvisor && !!this.resourcesForm.get('serviceadvisorIdControl')?.value;
    const hasDate = !!this.selectedDate && !!this.resourcesForm.get('preferredDateControl')?.value;
    const hasTimeslot = !!this.selectedTimeslot && !!this.resourcesForm.get('serviceAdvisorTimeslot')?.value;

    if (!hasDate) {
      this.showDateWarning = true;
    }
    if (hasDate && !hasTimeslot) {
      this.showTimeslotWarning = true;
    }

    return hasServiceAdvisor && hasDate && hasTimeslot;
  }

  getTimeSlots(type: string) {
    if (!this.selectedServiceListObject || this.selectedServiceListObject.length === 0) {
      return;
    }
    this.isLoading = true;
    this.datesErrorMessage = null;
    const requestId = ++this.timeslotRequestId;
    if (this.datesErrorTimer) {
      clearTimeout(this.datesErrorTimer);
      this.datesErrorTimer = null;
    }

    let resourceIds: (string | undefined)[] = [];
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : {};
    let departmentCode = '';
    if (sessionData?.vehicleInfo) {
      departmentCode = sessionData.vehicleInfo.type;
    }
    const sameDepartmentServices = (this.selectedServiceListObject ?? []).filter(
      s => s.department === departmentCode
    );

    if (type === 'serviceAdvisor') {
      resourceIds = this.selectedServiceAdvisor ? [this.selectedServiceAdvisor.id] : [''];
    }
    if (type === 'mobilityVehicle') {
      resourceIds = this.selectedMobilityVehicle ? [this.selectedMobilityVehicle.id] : [''];
    }

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    const dateFromCurrent = new Date(year, month, today.getDate(), 0, 0, 0);
    const lastDayCurrentMonth = new Date(year, month + 1, 0).getDate();
    const dateToCurrent = new Date(Date.UTC(year, month, lastDayCurrentMonth, 23, 59, 59));

    const dateFromNext = new Date(Date.UTC(year, month + 1, 1, 0, 0, 0));
    const lastDayNextMonth = new Date(year, month + 2, 0).getDate();
    const dateToNext = new Date(Date.UTC(year, month + 1, lastDayNextMonth, 23, 59, 59));

    const servicesWithIdOnly = sameDepartmentServices.map(s => {
      const service = new Service();
      service.id = s.id;
      return service;
    });

    const reqCurrentMonth = new TimeslotRequest();
    reqCurrentMonth.dateFrom = dateFromCurrent;
    reqCurrentMonth.dateTo = dateToCurrent;
    reqCurrentMonth.resourceIds = (resourceIds ?? []).filter(
      (id): id is string => id !== undefined
    );
    reqCurrentMonth.services = servicesWithIdOnly;

    const reqNextMonth = new TimeslotRequest();
    reqNextMonth.dateFrom = dateFromNext;
    reqNextMonth.dateTo = dateToNext;
    reqNextMonth.resourceIds = (resourceIds ?? []).filter(
      (id): id is string => id !== undefined
    );
    reqNextMonth.services = servicesWithIdOnly;

    forkJoin([
      this.appService.getTimeSlots(reqCurrentMonth).pipe(catchError(() => of([]))),
      this.appService.getTimeSlots(reqNextMonth).pipe(catchError(() => of([])))
    ]).subscribe(([response1, response2]) => {
      if (requestId !== this.timeslotRequestId) {
        return;
      }
      const allResponses = [
        ...(Array.isArray(response1) ? response1 : []),
        ...(Array.isArray(response2) ? response2 : [])
      ];
      if (allResponses.length === 0) {
        this.serviceAdvisorTimeslots = [];
        this.mobilityVehicleTimeslots = [];
        this.availableDates = [];
        this.availableTimeslots = [];
        this.selectedTimeslot = null;
        this.isLoading = false;
        this.datesErrorTimer = setTimeout(() => {
          if (requestId === this.timeslotRequestId && this.availableDates.length === 0) {
            this.datesErrorMessage = this.noDatesAvailable;
            this.cdr.markForCheck();
          }
        }, 700);
        this.cdr.markForCheck();
        return;
      }

      this.handleTimeSlotResponse(allResponses);
      if (this.datesErrorTimer) {
        clearTimeout(this.datesErrorTimer);
        this.datesErrorTimer = null;
      }
      this.datesErrorMessage = null;
      this.isLoading = false;
      this.cdr.markForCheck();
    },
      (error) => {
        this.appService.handleErrorMessage(error);
      });
  }

  private handleTimeSlotResponse(response: ITimeslot[]) {
    if (this.showServiceAdvisorTimesheets) {
      this.serviceAdvisorTimeslots = response;
    }
    if (this.showMobilityVehicleTimeSheets) {
      this.mobilityVehicleTimeslots = response;
    }

    const uniqueTimestamps = new Set<number>();
    response.forEach((slot: ITimeslot) => {
      if (slot.from) {
        const date = new Date(slot.from);
        uniqueTimestamps.add(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime());
      }
    });
    this.availableDates = Array.from(uniqueTimestamps).map(time => new Date(time));

    if (this.selectedDate) {
      this.loadTimeslotsForDate(this.selectedDate);
      if (this.selectedTimeslot) {
        const tsControl = this.resourcesForm.get('serviceAdvisorTimeslot');
        tsControl?.setValue(this.selectedTimeslot, { emitEvent: false });
        tsControl?.updateValueAndValidity();
        tsControl?.markAsUntouched();
      }
      this.resourcesForm.updateValueAndValidity();
      this.bookAppointmentService.updateAppointmentFormStatus(this.resourcesForm.valid);
    }
    this.cdr.markForCheck();
  }

  private filterDates(date: Date | null): boolean {
    if (!date) return false;
    const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    return this.availableDates.some(availDate =>
      new Date(availDate.getFullYear(), availDate.getMonth(), availDate.getDate()).getTime() === dateTime
    );
  }

  onServiceAdvisorSelect() {
    if (this.selectedServiceAdvisor) {
      this.showServiceAdvisorTimesheets = true;
      this.showMobilityVehicleTimeSheets = false;
    }
  }

  onMobilityVehicleSelect() {
    if (this.selectedMobilityVehicle) {
      this.showServiceAdvisorTimesheets = false;
      this.showMobilityVehicleTimeSheets = true;
    }
  }

  onServiceAdvisorChange(event: MatSelectChange) {
    type ExtendedResource = IResource & { Id?: string };
    const selectedServiceAdvisor = this.resourcesResponse.find((element: ExtendedResource) => element.id === event.value || element.Id === event.value)
    if (selectedServiceAdvisor) {
      this.selectedServiceAdvisor = selectedServiceAdvisor;

      const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
      const sessionData = sessionDataString ? JSON.parse(sessionDataString) : {};
      const updatedSessionData = {
        ...sessionData,
        appointment: {
          ...(sessionData?.appointment || {}),
          serviceAdvisorId: selectedServiceAdvisor.id,
          serviceAdvisorName: selectedServiceAdvisor.description
        }
      };
      this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(updatedSessionData));

      this.selectedDate = null;
      this.selectedTimeslot = null;
      this.availableTimeslots = [];
      this.resourcesForm.patchValue({
        preferredDateControl: null,
        serviceAdvisorTimeslot: null
      });

      this.getTimeSlots("serviceAdvisor");
      this.cdr.markForCheck();
    }
  }

  onMobilityVehicleChange(event: MatSelectChange) {
    type ExtendedResource = IResource & { Id?: string };
    const selectedMobilityVehicle = this.resourcesResponse.find((element: ExtendedResource) => element.Id === event.value || element.id === event.value)
    if (selectedMobilityVehicle) {
      this.selectedMobilityVehicle = selectedMobilityVehicle;
      this.getTimeSlots("mobilityVehicle");
      this.cdr.markForCheck();
    }
  }

  onDateSelected(date: Date) {
    this.selectedDate = date;
    const dateControl = this.resourcesForm.get('preferredDateControl');
    
    dateControl?.setValue(date, {
      emitEvent: true,
      onlySelf: true
    });
    dateControl?.markAsTouched();
    dateControl?.updateValueAndValidity({ onlySelf: true });
    
    this.selectedTimeslot = null;
    const timeslotControl = this.resourcesForm.get('serviceAdvisorTimeslot');
    timeslotControl?.setValue(null, { emitEvent: true, onlySelf: true });
    timeslotControl?.markAsUntouched();
    timeslotControl?.updateValueAndValidity({ onlySelf: true });
    
    this.saveSelectedDateToSession(date);
    this.loadTimeslotsForDate(date);
    this.showDateWarning = false;
    this.showTimeslotWarning = false;
    if (!this.validatorsRestored) {
      this.restoreValidators();
    }
    this.formTouched = true;
    this.updateFormValidation(true);
    this.resourcesForm.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  private saveSelectedDateToSession(date: Date): void {
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : {};
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const localDateString = `${year}-${month}-${day}`;

    const updatedSessionData = {
      ...sessionData,
      appointment: {
        ...sessionData.appointment,
        appointmentDate: localDateString,
        availableTimeslots: this.availableTimeslots,
        selectedTimeslot: this.selectedTimeslot
      }
    };

    this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(updatedSessionData));
  }

  private loadTimeslotsForDate(date: Date): void {
    const timeslots = this.showServiceAdvisorTimesheets ? this.serviceAdvisorTimeslots : this.mobilityVehicleTimeslots;

    const slotsForDate = timeslots.filter(slot => {
      const slotDate = new Date(slot.from!);
      return slotDate.toDateString() === date.toDateString();
    });

    this.availableTimeslots = slotsForDate.map(slot => {
      const fromTime = new Date(slot.from!);
      return `${this.formatTime(fromTime)}`;
    });

    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    if (sessionDataString) {
      const sessionData = JSON.parse(sessionDataString);
      const storedTimeslot: string | null = sessionData?.appointment?.selectedTimeslot || null;
      if (storedTimeslot && this.availableTimeslots.includes(this.reformatTimeslotString(storedTimeslot))) {
        this.selectedTimeslot = this.reformatTimeslotString(storedTimeslot);
        const timeslotControl = this.resourcesForm.get('serviceAdvisorTimeslot');
        timeslotControl?.setValue(this.selectedTimeslot, { emitEvent: true });
        timeslotControl?.updateValueAndValidity();
        this.resourcesForm.updateValueAndValidity();
        this.bookAppointmentService.updateAppointmentFormStatus(this.resourcesForm.valid);
      }
      const updatedSessionData = {
        ...sessionData,
        appointment: {
          ...sessionData.appointment,
          availableTimeslots: this.availableTimeslots
        }
      };
      this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(updatedSessionData));
    }

    this.createTimeslotPages();
    this.cdr.markForCheck();
  }

  private updateItemsPerPageForViewport(): void {
    const width = window.innerWidth;
    let newItemsPerPage: number;
    if (width <= 500) {
      newItemsPerPage = 6;
    } else if (width < 600) {
      newItemsPerPage = 9;
    } else {
      newItemsPerPage = 12;
    }

    if (this.itemsPerPage !== newItemsPerPage) {
      this.itemsPerPage = newItemsPerPage;
      if (this.availableTimeslots.length) {
        this.createTimeslotPages();
        this.cdr.markForCheck();
      }
    }
  }

  private updateLocaleCache(): void {
    const currentLocale = this.getCurrentLocale();
    if (this.localeCache.locale !== currentLocale) {
      this.localeCache.locale = currentLocale;
      this.localeCache.dateHint = this.computeDateHint(currentLocale);
      this.localeCache.hour12Pref = this.computeHour12Preference(currentLocale);
    }
  }

  private getCurrentLocale(): string {
    try {
      const stored = window.localStorage.getItem('lang');
      const candidate = stored || navigator?.language || 'en-US';
      try {
        new Intl.DateTimeFormat(candidate);
        return candidate;
      } catch {
        return 'en-US';
      }
    } catch {
      return 'en-US';
    }
  }

  getDateHint(): string {
    if (!this.localeCache.dateHint) {
      this.updateLocaleCache();
    }
    return this.localeCache.dateHint;
  }

  private computeDateHint(locale: string): string {
    const sample = new Date();
    try {
      const parts = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(sample);
      const tokenMap: Record<string, string> = { day: 'DD', month: 'MM', year: 'YYYY' };
      const ordered = parts
        .filter(p => p.type === 'day' || p.type === 'month' || p.type === 'year')
        .map(p => tokenMap[p.type]);
      if (ordered.length === 3) {
        return ordered.join('/');
      }
      return 'MM/DD/YYYY';
    } catch {
      return 'MM/DD/YYYY';
    }
  }

  private computeHour12Preference(locale: string): boolean | null {
    try {
      const resolved = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions();
      if (resolved.hourCycle === 'h11' || resolved.hourCycle === 'h12') return true;
      if (resolved.hourCycle === 'h23' || resolved.hourCycle === 'h24') return false;
    } catch { }
    try {
      const probe = new Date(2001, 0, 1, 13, 0, 0);
      const out = probe.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
      return /13/.test(out) ? false : true;
    } catch { }
    return null;
  }

  private formatTime(date: Date): string {
    if (!this.localeCache.locale) {
      this.updateLocaleCache();
    }

    const locale = this.localeCache.locale;
    const hour12Pref = this.localeCache.hour12Pref;

    try {
      return date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        ...(hour12Pref === null ? {} : { hour12: hour12Pref })
      } as Intl.DateTimeFormatOptions);
    } catch {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }

  private parseTimeslotToDate(timeslot: string): Date | null {
    if (!timeslot) return null;
    const trimmed = timeslot.trim();
    const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1], 10);
      const minutes = parseInt(ampmMatch[2], 10);
      const period = ampmMatch[3].toUpperCase();
      if (period === 'PM' && hours < 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      const d = new Date();
      d.setHours(hours, minutes, 0, 0);
      return d;
    }
    const h24Match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (h24Match) {
      const hours = parseInt(h24Match[1], 10);
      const minutes = parseInt(h24Match[2], 10);
      const d = new Date();
      d.setHours(hours, minutes, 0, 0);
      return d;
    }
    return null;
  }

  private reformatTimeslotString(timeslot: string): string {
    const d = this.parseTimeslotToDate(timeslot);
    return d ? this.formatTime(d) : timeslot;
  }

  onTimeslotSelected(timeslot: string): void {
    const timeslotControl = this.resourcesForm.get('serviceAdvisorTimeslot');
    
    if (this.selectedTimeslot === timeslot) {
      this.selectedTimeslot = null;
      timeslotControl?.setValue(null, { emitEvent: true });
      this.removeSelectedTimeslotFromSession();
    } else {
      this.selectedTimeslot = timeslot;
      timeslotControl?.setValue(timeslot, { emitEvent: true });

      const allTimeslots = [...this.serviceAdvisorTimeslots, ...this.mobilityVehicleTimeslots];
      const timeslotObj = allTimeslots.find(ts => {
        const date = ts.from ? new Date(ts.from) : null;
        return date ? this.formatTime(date) === timeslot : false;
      });
      
      this.saveSelectedTimeslotToSession(timeslot, timeslotObj?.fromUtcOffset);
    }
    
    timeslotControl?.markAsTouched();
    timeslotControl?.updateValueAndValidity({ onlySelf: true });
    
    this.showTimeslotWarning = !timeslot;
    if (!this.validatorsRestored) {
      this.restoreValidators();
    }
    this.formTouched = true;
    this.updateFormValidation(true);
    this.cdr.markForCheck();
  }

  private createTimeslotPages(): void {
    this.timeslotPages = [];
    this.currentPage = 0;

    for (let i = 0; i < this.availableTimeslots.length; i += this.itemsPerPage) {
      this.timeslotPages.push(this.availableTimeslots.slice(i, i + this.itemsPerPage));
    }
  }

  goToPage(pageIndex: number): void {
    if (pageIndex >= 0 && pageIndex < this.timeslotPages.length) {
      this.currentPage = pageIndex;
      this.cdr.markForCheck();
    }
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX;
  }

  onTouchMove(event: TouchEvent): void {
    event.preventDefault();
  }

  onTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].clientX;
    this.handleSwipe();
  }

  private handleSwipe(): void {
    const swipeDistance = this.touchStartX - this.touchEndX;

    if (Math.abs(swipeDistance) > this.minSwipeDistance) {
      if (swipeDistance > 0) {
        if (this.currentPage < this.timeslotPages.length - 1) {
          this.currentPage++;
          this.cdr.markForCheck();
        }
      } else {
        if (this.currentPage > 0) {
          this.currentPage--;
          this.cdr.markForCheck();
        }
      }
    }
  }

  private saveSelectedTimeslotToSession(timeslot: string, fromUtcOffset?: string): void {
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : {};

    const updatedSessionData = {
      ...sessionData,
      appointment: {
        ...sessionData.appointment,
        selectedTimeslot: timeslot,
        timeslotFromUtcOffset: fromUtcOffset || null
      }
    };

    this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(updatedSessionData));
  }

  private removeSelectedTimeslotFromSession(): void {
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : {};

    const updatedSessionData = {
      ...sessionData,
      appointment: {
        ...sessionData.appointment,
        selectedTimeslot: null
      }
    };

    this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(updatedSessionData));
  }

  OnServicechange() {
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : {};

    const updatedSessionData = {
      ...sessionData,
      appointment: {
        ...(sessionData?.appointment || {}),
        appointmentDate: null,
        selectedTimeslot: null
      }
    };

    this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(updatedSessionData));
    this.router.navigate(['/book-appointment/service']);
  }

  shouldShowDefault(serviceAdvisor: IResource): boolean {
    return serviceAdvisor.id === this.serviceAdvisorList?.[0]?.id;
  }

  getServiceAdvisorName(id: string | number): string {
    const advisor = this.serviceAdvisorList?.find(serviceAdvisor => serviceAdvisor.id === id);
    return advisor?.description ?? '';
  }

  updateSessionStorage(form: string) {
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : null;

    let appointmentDetails: any;
    if (sessionData && form === 'services') {
      appointmentDetails = {
        dealership: sessionData.dealership,
        vehicleInfo: sessionData.vehicleInfo,
        appointment: {
          services: sessionData.appointment?.services,
          serviceAdvisor: sessionData.appointment?.serviceAdvisor,
          serviceAdvisorTimeslot: sessionData.appointment?.serviceAdvisorTimeslot,
          mobilityVehicle: sessionData.appointment?.mobilityVehicle,
          mobilityVehicleTimeslot: sessionData.appointment?.mobilityVehicleTimeslot
        },
        customerInfo: sessionData.customerInfo
      }
    }
    else if (sessionData && form === 'serviceAdvisor') {
      this.resourcesForm.controls['serviceAdvisorTimeslot'].patchValue(this.timeslotForm.controls['serviceAdvisorTimeslot'].value);
      appointmentDetails = {
        dealership: sessionData.dealership,
        vehicleInfo: sessionData.vehicleInfo,
        appointment: {
          services: this.selectedServiceListObject,
          serviceAdvisor: this.selectedServiceAdvisor,
          serviceAdvisorTimeslot: this.timeslotForm.controls['serviceAdvisorTimeslot'].value,
          mobilityVehicle: sessionData.appointment?.mobilityVehicle,
          mobilityVehicleTimeslot: sessionData?.appointment.mobilityVehicleTimeslot
        },
        customerInfo: sessionData.customerInfo
      }
    }
    else if (sessionData && form === 'mobilityVehicle') {
      this.resourcesForm.controls['mobilityVehicleTimeslot'].patchValue(this.timeslotForm.controls['mobilityVehicleTimeslot'].value);
      appointmentDetails = {
        dealership: sessionData.dealership,
        vehicleInfo: sessionData.vehicleInfo,
        appointment: {
          services: this.selectedServiceListObject,
          serviceAdvisor: sessionData.appointment?.serviceAdvisor,
          serviceAdvisorTimeslot: sessionData.appointment?.serviceAdvisorTimeslot,
          mobilityVehicle: this.selectedMobilityVehicle,
          mobilityVehicleTimeslot: this.timeslotForm.controls['mobilityVehicleTimeslot'].value
        },
        customerInfo: sessionData.customerInfo
      }
    }
    else {
      appointmentDetails = {
        appointment: {
          services: this.selectedServiceListObject
        }
      }
    }
    this.sessionStorageService.setSessionItem('oabAppSessionData', JSON.stringify(appointmentDetails));
  }
}
